import { create } from "zustand";
import axios from "axios";

import useAppStore from "./useAppStore";
import useLayoutStore from "./useLayoutStore";
import i18n from "../i18n";

const API_URL = import.meta.env.VITE_API_BASE_URL;

axios.defaults.withCredentials = true;

const authT = (key) => i18n.t(key, { ns: "login" });

const serverErrorCodeMap = {
    AUTH_INVALID_CREDENTIALS: "invalidCredentials",
    PENDING_ACCOUNT_SETUP_RESET_FORBIDDEN: "errors.pendingSetupResetForbidden",
    SETUP_TOKEN_INVALID_OR_EXPIRED: "errors.setupSessionExpired",
    SETUP_TERMS_REQUIRED: "setup.mustAcceptTerms",
    SETUP_FAILED: "errors.setupFailed",
    FORGOT_EMAIL_AND_CODE_REQUIRED: "errors.codeRequired",
    FORGOT_INVALID_OR_EXPIRED_CODE: "errors.invalidOrExpiredCode",
    RESET_CODE_VERIFICATION_REQUIRED: "errors.codeVerificationRequired",
    RESET_USER_NOT_FOUND: "errors.resetUserNotFound",
    AUTH_INTERNAL_SERVER_ERROR: "errors.internalServerError",
    PASSWORD_MIN_LENGTH: "password.minLength",
    PASSWORD_UPPERCASE: "password.uppercase",
    PASSWORD_NUMBER: "password.number",
    PASSWORD_SPECIAL: "password.special",
    RATE_LIMIT_LOGIN_15M: "errors.loginRateLimit",
    RATE_LIMIT_DAILY: "errors.forgotRateLimit",
    RATE_LIMIT_STRICT: "errors.strictRateLimit",
};

const resolveAuthErrorMessage = (responseData, fallbackKey) => {
    if (typeof responseData === "string") {
        return responseData;
    }

    const mappedKey = serverErrorCodeMap[responseData?.errorCode];
    if (mappedKey) {
        if (mappedKey === "invalidCredentials") {
            return "Invalid credentials";
        }
        return authT(mappedKey);
    }

    if (responseData?.error) {
        return responseData.error;
    }

    if (responseData?.message) {
        return responseData.message;
    }

    return authT(fallbackKey);
};

const applyServerLanguage = (language) => {
    if (language === "en" || language === "sr") {
        useLayoutStore.getState().applyLanguage(language);
    }
};

const useAuthStore = create((set, get) => ({
    userRole: null,
    loading: true,
    error: null,
    userId: null,
    isHR: false,

    // Forgot password message
    forgotPasswordMessage: null,
    // Forgot password error
    // Za glavne greške,na njih se izbacuje samo modal sa porukom greške
    forgotPasswordError: null,
    // Forgot password loading
    forgotPasswordLoading: false,
    // Forgot password code success
    forgotPasswordCodeSuccess: false,
    // Forgot password code error
    forgotPasswordCodeError: null,
    // Forgot password reset success
    forgotPasswordResetSuccess: false,
    // Forgot password reset error
    forgotPasswordResetError: null,

    // Prvi setup naloga (lozinka + opšti uslovi)
    requiresAccountSetup: false,
    accountSetupToken: null,
    accountSetupEmail: null,
    accountSetupError: null,
    accountSetupLoading: false,

    // Provera sesije
    checkSession: async () => {
        set({ loading: true });
        try {
            const response = await axios.get(`${API_URL}/api/auth/me`);

            const employeesData = useAppStore.getState().employeesData

            if (!employeesData) {
                useAppStore.getState().initializeApp();
            }

            set({ userRole: response.data.userRole, userId: response.data.userId, isHR: response.data.isHR, loading: false });
            applyServerLanguage(response.data.language);
        } catch (error) {
            set({ userRole: null, isHR: false, loading: false });
        }
    },

    // Login
    login: async (username, password, navigate) => {
        try {
            const language = useLayoutStore.getState().language === "en" ? "en" : "sr";
            const response = await axios.post(`${API_URL}/api/auth/login`, { username, password, language });

            if (response.data.requiresSetup) {
                set({
                    requiresAccountSetup: true,
                    accountSetupToken: response.data.setupToken,
                    accountSetupEmail: response.data.email,
                    error: null,
                    accountSetupError: null,
                });
                return 'requiresSetup';
            }

            if (response.data.success) {
                set({
                    userRole: response.data.userRole,
                    error: null,
                    userId: response.data.userId,
                    isHR: response.data.isHR,
                    requiresAccountSetup: false,
                    accountSetupToken: null,
                    accountSetupEmail: null,
                    accountSetupError: null,
                });
                applyServerLanguage(response.data.language);
                useAppStore.getState().initializeApp();
                navigate(`/${response.data.userRole}`);
                return true;
            }
        } catch (error) {
            const responseData = error.response?.data;
            const errorCode = responseData?.errorCode;
            set({
                error: errorCode === "AUTH_INVALID_CREDENTIALS"
                    ? "Invalid credentials"
                    : responseData?.error === "Invalid credentials"
                    ? "Invalid credentials"
                    : resolveAuthErrorMessage(responseData, "errors.loginFailed"),
            });
            return false;
        }
    },

    clearAccountSetup: () => set({
        requiresAccountSetup: false,
        accountSetupToken: null,
        accountSetupEmail: null,
        accountSetupError: null,
        accountSetupLoading: false,
    }),

    completeAccountSetup: async (password, termsAccepted, navigate) => {
        const setupToken = get().accountSetupToken;

        if (!setupToken) {
            set({ accountSetupError: authT("errors.setupSessionExpired") });
            return false;
        }

        set({ accountSetupLoading: true, accountSetupError: null });

        try {
            const language = useLayoutStore.getState().language === "en" ? "en" : "sr";
            const response = await axios.post(`${API_URL}/api/auth/complete-account-setup`, {
                setupToken,
                password,
                termsAccepted,
                language,
            });

            if (response.data.success) {
                set({
                    userRole: response.data.userRole,
                    userId: response.data.userId,
                    isHR: response.data.isHR,
                    requiresAccountSetup: false,
                    accountSetupToken: null,
                    accountSetupEmail: null,
                    accountSetupError: null,
                    accountSetupLoading: false,
                    error: null,
                });
                applyServerLanguage(response.data.language || language);
                useAppStore.getState().initializeApp();
                navigate(`/${response.data.userRole}`);
                return true;
            }

            set({ accountSetupLoading: false });
            return false;
        } catch (error) {
            const responseData = error.response?.data;
            const errorMessage = resolveAuthErrorMessage(responseData, "errors.setupFailed");
            set({ accountSetupError: errorMessage, accountSetupLoading: false });
            return false;
        }
    },

    // Logout
    logout: async () => {
        try {
            await axios.get(`${API_URL}/api/auth/logout`);
            const resetAppState = useAppStore.getState().resetAppState;
            if (typeof resetAppState === 'function') {
                resetAppState();
            }
            set({
                userRole: null,
                userId: null,
                isHR: false
            });
        } catch (error) {
            console.error('Logout error:', error);
        }
    },

    // Reset error
    resetError: () => set({ error: null }),

    // Forgot password
    forgotPasswordSendMail: async (email) => {
        set({ forgotPasswordLoading: true, forgotPasswordMessage: null });
        try {
            const language = useLayoutStore.getState().language === 'en' ? 'en' : 'sr';
            const response = await axios.post(`${API_URL}/api/auth/forgot-password`, { email, language });
            console.log(response);
            set({ forgotPasswordMessage: { message: response.data.message, email: response.data.email }, forgotPasswordLoading: false });
        } catch (error) {
            const responseData = error.response?.data;
            const errorMessage = resolveAuthErrorMessage(responseData, "errors.forgotGeneric");
            const forgotPasswordError = { message: errorMessage, status: error.response?.status };
            set({ forgotPasswordError: forgotPasswordError, forgotPasswordLoading: false });
        }
    },

    // Forgot password submit code
    forgotPasswordSubmitCode: async (code) => {
        set({ forgotPasswordLoading: true, forgotPasswordCodeError: null });
        try {
            const language = useLayoutStore.getState().language === 'en' ? 'en' : 'sr';
            const response = await axios.post(`${API_URL}/api/auth/forgot-password-submit-code`, {
                code,
                email: get().forgotPasswordMessage.email,
                language
            });
            console.log(response);
            if (response.data.success) {
                set({ forgotPasswordCodeSuccess: true, forgotPasswordLoading: false, forgotPasswordCodeError: null });
            } else {
                set({ forgotPasswordLoading: false });
            }
        } catch (error) {
            console.log(error);
            const status = error.response?.status;
            const responseData = error.response?.data;
            const errorMessage = resolveAuthErrorMessage(responseData, "errors.codeGeneric");

            if (status === 429) {
                // Glavni limiter (24h zabrana) - setuje glavnu grešku
                const forgotPasswordError = {
                    message: errorMessage || authT("errors.forgotRateLimit"),
                    status: status
                };
                set({ forgotPasswordError: forgotPasswordError, forgotPasswordLoading: false, forgotPasswordCodeError: null });
            } else if (status === 401) {
                // Pogrešan ili istekao kod
                set({ forgotPasswordLoading: false, forgotPasswordCodeError: errorMessage });
            } else {
                // Sve ostale greške
                set({ forgotPasswordLoading: false, forgotPasswordCodeError: errorMessage });
            }
        }
    },

    // Reset password
    resetPassword: async (newPassword) => {
        set({ forgotPasswordLoading: true, forgotPasswordResetError: null });
        try {
            const language = useLayoutStore.getState().language === 'en' ? 'en' : 'sr';
            const response = await axios.post(`${API_URL}/api/auth/reset-password`, { newPassword, language });
            if (response.data.success) {
                set({ forgotPasswordResetSuccess: true, forgotPasswordLoading: false });
                return true;
            } else {
                set({ forgotPasswordLoading: false });
                return false;
            }
        } catch (error) {
            console.log(error);
            const errorMessage = resolveAuthErrorMessage(error.response?.data, "errors.resetFailed");
            set({ forgotPasswordResetError: errorMessage, forgotPasswordLoading: false, forgotPasswordResetSuccess: false });
            return false;
        }
    },
}));

export default useAuthStore;
